import { CONFIG } from '../config.js';
import { safeFetchClient } from '../utils/safe-fetch-client.js';
import { getCurrentSession } from './auth.service.js';
import { listLiquidaciones as repoListLiquidaciones } from '../infrastructure/liquidaciones.repository.js';
import { MOTIVO_MAX_LEN } from '../shared/timesheet-policy.js';
import { mensajeJornadasAbiertasLiquidacion } from '../shared/fichadas-ui-policy.js';
import { mensajeFijoYaLiquidadoMes } from '../shared/fichadas-ui-policy.js';

/**
 * Servicio de liquidación de sueldos (Control de Horarios y Liquidación de
 * Sueldos, slice 5). A diferencia del resto de los servicios admin de este
 * panel, NO habla con Supabase directo (RLS + anon key): `POST
 * /api/liquidacion` corre server-side con la service_role key (necesita los
 * secretos de Fudo + `create_liquidacion`), así que este servicio manda el
 * `access_token` de la sesión de staff activa como Bearer y el endpoint lo
 * verifica él mismo (`api/_lib/admin-auth.js`, AD6) — es el primer fetch
 * admin de este repo que se autentica con un JWT en vez de dejar que RLS
 * decida.
 *
 * `confirmLiquidacion` NUNCA manda ningún monto: el body solo lleva
 * `empleado_ids`/`desde`/`hasta`, igual que `preview` — `api/liquidacion.js`
 * recalcula todo server-side, así que este servicio no tiene forma de
 * "tamperear" nada aunque quisiera.
 *
 * E2: `loadLiquidacionesHistorial` es la ÚNICA función de este archivo que sí
 * habla con Supabase — vía `liquidaciones.repository.js#listLiquidaciones`
 * (browser, anon key, RLS `liquidaciones_admin_read`). Es de PANTALLA
 * (historial + panel de consumo del tope) y AD9 la marca explícitamente
 * NUNCA load-bearing para el cálculo de sueldos, que sigue viviendo
 * server-side en `api/liquidacion.js` — no cambia el criterio del resto del
 * archivo, sólo agrega una lectura de sólo-pantalla.
 *
 * CERO SILENCIOSO (AD7, clase de bug recurrente en este change): un 503
 * (Fudo sin credenciales) o un 502 (contrato de Fudo roto) NUNCA se
 * traducen a "$0 de descuentos" acá — se devuelven con su propio `kind`
 * bloqueante (`fudo_bloqueado`), y `LiquidacionView` los tiene que mostrar
 * como una liquidación que NO PUDO CORRER, nunca como un resultado con
 * descuentos en cero.
 */

const ERROR_MESSAGES = {
    unauthorized: 'Tu sesión no es válida. Volvé a iniciar sesión.',
    forbidden: 'No tenés permiso para liquidar sueldos.',
    accion_invalida: 'Acción inválida.',
    empleado_ids_requerido: 'Elegí al menos un empleado.',
    rango_invalido: 'El rango de fechas no es válido.',
    // L1/D2 (owner decisions 2026-09-27): confirm rechaza un `hasta` de hoy
    // (ART) o posterior (`periodoTerminado`). Preview sigue permitido.
    periodo_no_terminado: 'Solo se pueden liquidar días que ya terminaron: usá una fecha final de ayer o antes.',
    empleado_inexistente: 'Uno de los empleados seleccionados no existe.',
    no_disponible: 'El servicio no está disponible ahora. Probá de nuevo en un momento.',
    fudo_no_configurado: 'No se pudo conectar con Fudo — no se descontó nada. No es posible liquidar en este momento.',
    fudo_contrato_invalido: 'Los datos de Fudo son inconsistentes — no se descontó nada. No es posible liquidar hasta resolverlo.',
    // Código real de create_liquidacion ante un reintento (choque con el
    // índice único). Sin esta entrada caía en error_interno ("probá de
    // nuevo"), el peor consejo posible: le decía al admin que reintentara
    // algo que YA está liquidado.
    periodo_duplicado: 'Este empleado ya tiene una liquidación CONFIRMADA para ese período — no hace falta reintentar.',
    // D2 correction, follow-up C3: fase 1b (503), tope mensual sin
    // configurar. Es un problema de configuración, no una falla
    // transitoria — reintentar NUNCA lo resuelve. `payroll_config` está
    // vacía en producción hoy: este es el PRIMER error que va a ver un
    // operador apenas se prenda el módulo.
    tope_no_configurado: 'El tope mensual de descuento todavía no está configurado — no es posible liquidar sueldos hasta que un admin lo cargue. No se calculó ni se guardó nada.',
    // D2 correction, follow-up C2: fase 1c (409 periodo_solapado) y fase 1d
    // (409 liquidacion_previa_sin_descuento) corren para TODO el batch
    // ANTES de la fase de persistencia — un rechazo acá significa que NADA
    // se escribió todavía, para nadie. `mapMensaje` elige el texto según
    // el código real; el kind (más abajo) es el mismo para los dos porque
    // la clase de bloqueo es la misma: precondición del batch, sin
    // escritura, hay que resolverla antes de reintentar.
    periodo_solapado: 'Este período se superpone con una liquidación ya registrada para alguno de los empleados elegidos — no se guardó nada. Elegí otro rango o revisá la liquidación existente antes de reintentar.',
    liquidacion_previa_sin_descuento: 'Hay una liquidación anterior en el mismo mes que todavía no tiene registrado el descuento del tope (quedó guardada antes de esa función) — no se guardó nada nuevo. Hay que regularizarla antes de poder liquidar este mes otra vez.',
    // C1 (migración 24, concurrencia optimista del tope mensual): otro
    // 'confirm' para el mismo empleado y mes ART se coló y confirmó mientras
    // ÉSTE se estaba preparando — el excedente que se intentó guardar estaba
    // calculado sobre una foto vieja de liquidaciones previas.
    // create_liquidacion lo rechazó ANTES de escribir nada para este
    // empleado (mismo mecanismo de rechazo-durante-la-persistencia que
    // periodo_duplicado, ver `liquidaciones_persistidas`/
    // `empleados_no_intentados` más abajo en `postLiquidacion`) — nunca cae
    // al genérico "Probá de nuevo": ese mensaje ya le costó a esta cadena
    // repetir el mismo defecto dos veces (un código de error nuevo del
    // servidor que se perdía en error_interno). El operador necesita saber
    // QUÉ pasó (otra liquidación se confirmó primero) y QUÉ hacer (volver a
    // previsualizar, no reintentar a ciegas con los mismos números).
    tope_desactualizado: 'Otra liquidación del mismo empleado para el mismo mes se confirmó mientras se preparaba ésta — el cálculo del tope quedó desactualizado y no se guardó nada para este empleado. Volvé a previsualizar antes de confirmar de nuevo.',
    // R4-001: la escritura en sí falló en tránsito (no un rechazo de
    // create_liquidacion) — ambiguo por definición, mismo criterio que la
    // falla de transporte del fetch: nunca "probá de nuevo" sin más.
    create_liquidacion_transporte: 'Se cortó la conexión al guardar esta liquidación. No sabemos si quedó guardada — revisá antes de reintentar SOLO este empleado.',
    tiempo_agotado: 'No se pudo completar a tiempo — no se guardó nada. Es seguro reintentar.',
    // F-F: respaldo de `jornadas_abiertas` cuando el 409 no trae `detalle`
    // legible — el mensaje normal nombra a cada empleado (ver `postLiquidacion`).
    jornadas_abiertas: 'Hay turnos sin salida en este período. Cerralos antes de liquidar.',
    // L10: respaldo de `fijo_ya_liquidado_mes` cuando el 409 no trae `detalle`
    // legible — el mensaje normal nombra a cada empleado y el mes.
    fijo_ya_liquidado_mes: 'Un sueldo fijo se liquida una vez por mes, y alguno de los empleados elegidos ya lo tiene liquidado en ese mes.',
    // Unit B1 (design.md AD3/AD10/AD11) — anulación auditada de una
    // liquidación confirmada. Los cuatro primeros son los códigos que
    // anular_liquidacion (migración 27) devuelve como DATO de negocio; el
    // quinto (admin_id_invalido) es defensivo — api/liquidacion.js lo
    // traduce hoy a error_interno (AD10: un código de negocio inesperado en
    // este punto es un bug de infraestructura, no algo que el admin causó),
    // pero se documenta acá para no colapsar en el fallback genérico si esa
    // traducción cambia alguna vez.
    liquidacion_id_requerido: 'No se pudo identificar la liquidación a anular.',
    motivo_requerido: 'Ingresá un motivo para anular la liquidación.',
    // Follow-up (review-5b84ba2ca338f75a, resilience WARNING): `motivo` no
    // tenía cota — `MOTIVO_MAX_LEN` (js/shared/timesheet-policy.js, con su
    // gemelo de paridad en api/_lib) la fija en las dos capas.
    motivo_demasiado_largo: `El motivo no puede tener más de ${MOTIVO_MAX_LEN} caracteres.`,
    liquidacion_inexistente: 'Esa liquidación no existe.',
    liquidacion_ya_anulada: 'Esta liquidación ya fue anulada anteriormente.',
    admin_id_invalido: 'No se pudo identificar al administrador que anula. Volvé a iniciar sesión e intentá de nuevo.',
    error_interno: 'Ocurrió un error. Probá de nuevo.',
};

/**
 * @param {string|undefined} code
 * @returns {string}
 */
function mapMensaje(code) {
    return ERROR_MESSAGES[code] ?? ERROR_MESSAGES.error_interno;
}

/**
 * 5xx codes that api/liquidacion.js answers ON PURPOSE, each one with a known
 * outcome (nothing written, or a configuration block). Any other 5xx on a
 * WRITE (`confirm`/`anular`) is ambiguous: a gateway 502/504 (function
 * timeout), a non-JSON body, or the server's catch-all 500 `error_interno`
 * can all arrive after the RPC already committed. `error_interno` is NOT in
 * this set on purpose — its "Probá de nuevo" is exactly the blind-retry
 * invitation this rule removes. `admin_id_invalido` is not here either: the
 * server translates it to `error_interno` and never answers it at 5xx. Kept
 * in parity with the server by tests/unit/liquidacion-5xx-parity.test.js.
 */
export const KNOWN_5XX_CODES = new Set([
    'no_disponible',
    'fudo_no_configurado',
    'fudo_contrato_invalido',
    'tope_no_configurado',
    'tiempo_agotado',
]);

const INCIERTO_5XX_MSG = 'No sabemos si se aplicó — revisá el historial antes de reintentar.';

/**
 * @param {number} status
 * @param {string|undefined} code
 * @returns {boolean}
 */
function esCincoXXAmbiguo(status, code) {
    return status >= 500 && !KNOWN_5XX_CODES.has(code);
}

/**
 * @typedef {{ ok: true, liquidaciones: Array<object> } | {
 *   ok: false,
 *   kind: 'auth'|'validacion'|'anomalias'|'fudo_bloqueado'|'tope_no_configurado'|'precondicion_bloqueante'|'rechazo_parcial'|'incierto'|'error',
 *   mensaje: string,
 *   anomalias?: Array<{ empleado_id: string, anomalias: Array<{ fichadaId: string, tipo: string }> }>,
 *   detalle?: Array<{ empleado_id: string, liquidacion_id: string, periodo_desde?: string, periodo_hasta?: string }>,
 *   jornadasAbiertas?: Array<{ empleado_id: string, nombre: string, fichadas: Array<{ id: string, entrada_at: string }> }>,
 *   empleadoId?: string,
 *   liquidacionesPersistidas?: Array<object>,
 *   empleadosNoIntentados?: string[],
 *   errorCode?: string,
 * }} LiquidacionResult
 */

/**
 * POST /api/liquidacion con el Bearer token de la sesión de staff activa.
 * Único punto de fetch de este servicio — `previewLiquidacion`/
 * `confirmLiquidacion` solo fijan `action`.
 * @param {'preview'|'confirm'} action
 * @param {{ empleadoIds: string[], desde: string, hasta: string }} params
 * @returns {Promise<LiquidacionResult>}
 */
async function postLiquidacion(action, { empleadoIds, desde, hasta } = {}) {
    const session = await getCurrentSession();
    if (!session?.access_token) {
        return { ok: false, kind: 'auth', mensaje: ERROR_MESSAGES.unauthorized };
    }

    let res;
    try {
        res = await safeFetchClient(CONFIG.API.LIQUIDACION, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                Authorization: `Bearer ${session.access_token}`,
            },
            body: JSON.stringify({ action, empleado_ids: empleadoIds, desde, hasta }),
            timeoutMs: 55_000, // el endpoint puede paginar un mes de /sales; ver vercel.json maxDuration:60
        });
    } catch {
        // Una falla de transporte en CONFIRM es ambigua por definición —
        // no hay forma de distinguir "no llegó a escribir nada" de "escribió
        // y la respuesta se perdió" (abort de 55s, corte de red). Nunca se
        // presenta como "error, probá de nuevo": eso invitaría a reintentar
        // un batch que puede haber quedado a mitad de camino. `preview` no
        // escribe nada, así que ahí un error genérico sigue siendo seguro.
        if (action === 'confirm') {
            return {
                ok: false,
                kind: 'incierto',
                mensaje: 'Se cortó la conexión antes de confirmar. No sabemos si la liquidación se guardó — revisá la sección antes de reintentar, no confirmes el batch entero a ciegas.',
            };
        }
        return { ok: false, kind: 'error', mensaje: ERROR_MESSAGES.error_interno };
    }

    const body = await res.json().catch(() => ({}));

    if (res.ok) {
        return { ok: true, liquidaciones: body.liquidaciones ?? [] };
    }

    if (res.status === 401 || res.status === 403) {
        return { ok: false, kind: 'auth', mensaje: mapMensaje(body.error), errorCode: body.error };
    }

    if (res.status === 409 && body.error === 'anomalias_pendientes') {
        return { ok: false, kind: 'anomalias', mensaje: '', anomalias: body.detalle ?? [], errorCode: body.error };
    }
    if (res.status === 409 && (body.error === 'periodo_solapado' || body.error === 'liquidacion_previa_sin_descuento')) {
        // D2 correction, follow-up C2: fases 1c/1d son un gate del BATCH
        // COMPLETO, corren antes que la fase de persistencia — a diferencia
        // de `rechazo_parcial` (más abajo), acá NUNCA hay nada ya escrito.
        // `body.detalle` nombra cada fila/empleado involucrado; nunca se
        // descarta. NO reusar `rechazo_parcial`: le mentiría al operador
        // sobre un guardado parcial que no ocurrió.
        return {
            ok: false,
            kind: 'precondicion_bloqueante',
            mensaje: mapMensaje(body.error),
            detalle: body.detalle ?? [],
            errorCode: body.error,
        };
    }
    if (res.status === 409 && body.error === 'fijo_ya_liquidado_mes') {
        // L10: gate de confirm (fase 1e) — corre antes de escribir nada, misma
        // clase que periodo_solapado. `detalle` trae `{ empleado_id, nombre,
        // mes, liquidacion_id, periodo_desde, periodo_hasta }`: el panel de
        // confirm lista la liquidación existente de cada uno.
        const detalle = Array.isArray(body.detalle) ? body.detalle : [];
        const mensajes = detalle.filter((d) => d?.mes).map((d) => mensajeFijoYaLiquidadoMes(d.nombre ?? 'Empleado', d.mes));
        return {
            ok: false,
            kind: 'precondicion_bloqueante',
            mensaje: mensajes.length > 0 ? mensajes.join(' ') : ERROR_MESSAGES.fijo_ya_liquidado_mes,
            detalle,
            errorCode: body.error,
        };
    }
    if (res.status === 409 && body.error === 'jornadas_abiertas') {
        // F-F: gate de confirm (fase 1a) — corre antes de escribir nada,
        // misma clase que periodo_solapado. `detalle` del server trae
        // `{ empleado_id, nombre, fichadas: [{ id, entrada_at }] }`; va en
        // `jornadasAbiertas`, NUNCA en `detalle`: el panel de confirm pinta
        // `detalle` como una lista de liquidaciones previas.
        const items = (Array.isArray(body.detalle) ? body.detalle : []).flatMap((d) =>
            (Array.isArray(d?.fichadas) ? d.fichadas : []).map((f) => ({ nombre: d.nombre ?? 'Empleado', entrada_at: f.entrada_at })),
        );
        return {
            ok: false,
            kind: 'precondicion_bloqueante',
            mensaje: items.length > 0 ? mensajeJornadasAbiertasLiquidacion(items) : ERROR_MESSAGES.jornadas_abiertas,
            jornadasAbiertas: body.detalle ?? [],
            errorCode: body.error,
        };
    }
    if (res.status === 409) {
        // Rechazo de negocio de create_liquidacion durante confirm (p.ej.
        // periodo_duplicado). `liquidaciones_persistidas` es lo que YA quedó
        // escrito antes del rechazo — se propaga tal cual, nunca se descarta.
        return {
            ok: false,
            kind: 'rechazo_parcial',
            mensaje: mapMensaje(body.error),
            empleadoId: body.empleado_id,
            liquidacionesPersistidas: body.liquidaciones_persistidas ?? [],
            empleadosNoIntentados: body.empleados_no_intentados ?? [], // nunca se llegó a pedirles create_liquidacion
            errorCode: body.error,
        };
    }

    // Owner decision (ambiguous server errors): on CONFIRM an unrecognised
    // 5xx may have landed after some create_liquidacion already committed —
    // same class as the transport failure above. Preview never writes, so it
    // keeps the plain mapping below.
    if (action === 'confirm' && esCincoXXAmbiguo(res.status, body.error)) {
        return { ok: false, kind: 'incierto', mensaje: INCIERTO_5XX_MSG, errorCode: body.error };
    }

    if (res.status === 502 || (res.status === 503 && body.error === 'fudo_no_configurado')) {
        return { ok: false, kind: 'fudo_bloqueado', mensaje: mapMensaje(body.error), errorCode: body.error };
    }
    if (res.status === 503 && body.error === 'tope_no_configurado') {
        // D2 correction, follow-up C3: mismo tratamiento que fudo_bloqueado
        // (problema de configuración, no falla transitoria) pero con su
        // propio kind — el mensaje y la UI apuntan a "hay que configurar
        // el tope", no a "Fudo está caído".
        return { ok: false, kind: 'tope_no_configurado', mensaje: mapMensaje(body.error), errorCode: body.error };
    }

    if (res.status === 400 || res.status === 404) {
        return { ok: false, kind: 'validacion', mensaje: mapMensaje(body.error), errorCode: body.error };
    }

    return { ok: false, kind: 'error', mensaje: mapMensaje(body.error), errorCode: body.error };
}

/**
 * Preview: computa la liquidación de cada empleado seleccionado SIN
 * persistir nada.
 * @param {{ empleadoIds: string[], desde: string, hasta: string }} params
 * @returns {Promise<LiquidacionResult>}
 */
export async function previewLiquidacion(params) {
    return postLiquidacion('preview', params);
}

/**
 * Confirm: RECOMPUTA todo server-side (api/liquidacion.js nunca lee montos
 * del body) y persiste una fila `liquidaciones` por empleado.
 * @param {{ empleadoIds: string[], desde: string, hasta: string }} params
 * @returns {Promise<LiquidacionResult>}
 */
export async function confirmLiquidacion(params) {
    return postLiquidacion('confirm', params);
}

/**
 * Anulación auditada de una liquidación CONFIRMADA (Unit B1, design.md
 * AD3/AD10/AD11). Distinta forma de body que preview/confirm (`liquidacion_id`
 * + `motivo`, nunca `empleado_ids`/`desde`/`hasta`), así que no reusa
 * `postLiquidacion` — tiene su propio mapeo de respuesta, más simple: no hay
 * batch, no hay `liquidaciones_persistidas`/`empleados_no_intentados`, es UNA
 * fila o nada.
 *
 * El `motivo` se valida ACÁ primero (UX only — el server revalida igual,
 * AD3) para no mandar un request que el server va a rechazar de todos modos.
 * @param {{ liquidacionId: string, motivo: string }} params
 * @returns {Promise<
 *   | { ok: true }
 *   | { ok: false, kind: 'auth'|'validacion'|'incierto'|'error', mensaje: string, errorCode?: string }
 * >}
 */
export async function anularLiquidacion({ liquidacionId, motivo } = {}) {
    const motivoTrim = typeof motivo === 'string' ? motivo.trim() : '';
    if (motivoTrim.length === 0) {
        return { ok: false, kind: 'validacion', mensaje: ERROR_MESSAGES.motivo_requerido, errorCode: 'motivo_requerido' };
    }
    // Follow-up (review-5b84ba2ca338f75a, resilience WARNING): mismo
    // criterio que `motivo_requerido` — se valida ACÁ primero (UX only,
    // AD3) para no mandar un request que `parsePayload` va a rechazar de
    // todos modos con el mismo código.
    if (motivoTrim.length > MOTIVO_MAX_LEN) {
        return { ok: false, kind: 'validacion', mensaje: ERROR_MESSAGES.motivo_demasiado_largo, errorCode: 'motivo_demasiado_largo' };
    }

    const session = await getCurrentSession();
    if (!session?.access_token) {
        return { ok: false, kind: 'auth', mensaje: ERROR_MESSAGES.unauthorized };
    }

    let res;
    try {
        res = await safeFetchClient(CONFIG.API.LIQUIDACION, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                Authorization: `Bearer ${session.access_token}`,
            },
            body: JSON.stringify({ action: 'anular', liquidacion_id: liquidacionId, motivo: motivoTrim }),
            timeoutMs: 55_000,
        });
    } catch {
        // Follow-up (review-5b84ba2ca338f75a, resilience WARNING): esto SÍ es
        // una falla de transporte ambigua por definición, igual que la que
        // `postLiquidacion` ya trata como `incierto` en `confirm` (ver ese
        // comentario) — no hay forma de distinguir "la RPC nunca corrió" de
        // "corrió y la respuesta se perdió" (corte de red, timeout). Un
        // `kind: 'error'` genérico invitaría a reintentar a ciegas una
        // transición de estado que puede haber quedado aplicada — reintentar
        // una anulación que SÍ se aplicó sólo vuelve a pegar contra
        // `liquidacion_ya_anulada`, pero el admin no tiene forma de saberlo
        // sin revisar el historial primero.
        return {
            ok: false,
            kind: 'incierto',
            mensaje: 'Se cortó la conexión al anular. No sabemos si se aplicó — revisá el historial antes de reintentar.',
        };
    }

    const body = await res.json().catch(() => ({}));

    if (res.ok) {
        return { ok: true };
    }
    if (res.status === 401 || res.status === 403) {
        return { ok: false, kind: 'auth', mensaje: mapMensaje(body.error), errorCode: body.error };
    }
    // 404/409/400 son los tres rechazos de negocio que anular_liquidacion
    // puede devolver (liquidacion_inexistente/liquidacion_ya_anulada/
    // motivo_requerido) — SIEMPRE 'validacion', nunca 'rechazo_parcial': acá
    // no hay noción de batch ni de escritura parcial, es una fila o ninguna.
    if (res.status === 404 || res.status === 409 || res.status === 400) {
        return { ok: false, kind: 'validacion', mensaje: mapMensaje(body.error), errorCode: body.error };
    }
    // Owner decision (ambiguous server errors): same rule as confirm — the
    // catch-all 500 also covers an RPC whose response was lost after commit.
    if (esCincoXXAmbiguo(res.status, body.error)) {
        return { ok: false, kind: 'incierto', mensaje: INCIERTO_5XX_MSG, errorCode: body.error };
    }

    return { ok: false, kind: 'error', mensaje: mapMensaje(body.error), errorCode: body.error };
}

/**
 * E2 — historial de liquidaciones confirmadas para el panel informativo
 * (AD9: display-only, browser/anon key — NUNCA autoritativo para el cálculo
 * de sueldos). Thin pass-through, sin try/catch a propósito (mismo criterio
 * que `admin-empleados.service.js#loadEmpleados`): el caller (`js/admin.js`)
 * es quien atrapa una falla real de Supabase y degrada SOLO este panel,
 * igual que ya hace con `refreshLiquidacionCapConfig`/`loadPayrollConfig`.
 * Filtros: ver `liquidaciones.repository.js#listLiquidaciones` (L1/L6/L8).
 * @param {{ inicioDesde?: string, inicioHasta?: string, inicioAntesDe?: string, offset?: number, limit?: number }} [params]
 * @returns {Promise<Array<object>>}
 * @throws {Error} si Supabase responde con error real
 */
export async function loadLiquidacionesHistorial(params = {}) {
    return repoListLiquidaciones(params);
}
